import { FormEvent, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { DashboardPage } from '../features/auth/DashboardPage';
import { LoginPage } from '../features/auth/LoginPage';
import { LeadsPage } from '../features/crm/LeadsPage';
import { LeadDetailPage } from '../features/crm/LeadDetailPage';
import { CustomersPage } from '../features/crm/CustomersPage';
import { CustomerDetailPage } from '../features/crm/CustomerDetailPage';
import { ServicesPage } from '../features/catalog/ServicesPage';
import { ServiceDetailPage } from '../features/catalog/ServiceDetailPage';
import { OffersPage } from '../features/catalog/OffersPage';
import { OrganizationPage } from '../features/organization/OrganizationPage';
import { ProfessionalsPage } from '../features/professionals/ProfessionalsPage';
import { IamPage } from '../features/admin/IamPage';
import { OperationsPage } from '../features/operations/OperationsPage';
import { AccountingPage } from '../features/accounting/AccountingPage';
import { ReportingPage } from '../features/reporting/ReportingPage';
import { HrPage } from '../features/hr/HrPage';
import { MarketingPage } from '../features/marketing/MarketingPage';
import { useAuth } from '../context/AuthContext';
import { ScopeProvider, useScope } from '../context/ScopeContext';
import { useDirection } from '../context/DirectionContext';
import type { TranslationKey } from '../i18n/catalog';

type MenuItem = { to: string; label: TranslationKey; permission?: string; icon: string };
type MenuGroup = { label: TranslationKey; icon: string; permission?: string; items: MenuItem[] };

const directItems: MenuItem[] = [
  { to: '/', label: 'dashboard', icon: 'dashboard' },
  { to: '/operations#booking', label: 'appointments', permission: 'sales.invoice.create', icon: 'calendar' },
  { to: '/marketing', label: 'marketing', permission: 'marketing.manage', icon: 'bullhorn' },
  { to: '/accounting', label: 'accounting', permission: 'finance.coa.view', icon: 'calculator' },
];

const groups: MenuGroup[] = [
  {
    label: 'subscriptions', icon: 'users',
    items: [
      { to: '/crm/leads', label: 'leads', permission: 'crm.lead.view', icon: 'lead' },
      { to: '/crm/customers', label: 'customers', permission: 'crm.customer.view', icon: 'customer' },
    ],
  },
  {
    label: 'masterData', icon: 'cog',
    items: [
      { to: '/catalog/services', label: 'services', permission: 'catalog.view', icon: 'service' },
      { to: '/catalog/offers', label: 'offers', permission: 'catalog.view', icon: 'tag' },
      { to: '/professionals', label: 'professionals', permission: 'professional.view', icon: 'doctor' },
    ],
  },
  {
    label: 'humanResources', icon: 'tie', permission: 'employee.view',
    items: [
      { to: '/hr#attendance', label: 'attendance', permission: 'employee.view', icon: 'fingerprint' },
      { to: '/hr#leaves', label: 'leaves', permission: 'employee.view', icon: 'umbrella' },
      { to: '/hr#finance', label: 'employeeFinance', permission: 'employee.view', icon: 'money' },
      { to: '/hr#payroll', label: 'payroll', permission: 'employee.view', icon: 'wallet' },
    ],
  },
  {
    label: 'reportCenter', icon: 'report', permission: 'report.operational.view',
    items: [
      { to: '/reports?report=overview', label: 'managementOverview', permission: 'report.operational.view', icon: 'chart' },
      { to: '/reports?report=service-revenue', label: 'serviceRevenue', permission: 'report.operational.view', icon: 'chart' },
      { to: '/reports?report=payments', label: 'paymentRegister', permission: 'report.operational.view', icon: 'money' },
      { to: '/reports?report=ar-aging', label: 'arAging', permission: 'report.operational.view', icon: 'clock' },
      { to: '/reports?report=professional-sessions', label: 'professionalSessions', permission: 'report.operational.view', icon: 'doctor' },
      { to: '/reports?report=inventory-consumption', label: 'inventoryConsumption', permission: 'report.operational.view', icon: 'box' },
    ],
  },
  {
    label: 'administration', icon: 'shield',
    items: [
      { to: '/admin/organization', label: 'organization', permission: 'organization.view', icon: 'building' },
      { to: '/admin/iam', label: 'iam', permission: 'admin.iam.manage', icon: 'lock' },
    ],
  },
];

const iconGlyphs: Record<string, string> = {
  dashboard: '▦', calendar: '▣', bullhorn: '◖', calculator: '▤', users: '♟', lead: '◎', customer: '●',
  cog: '⚙', service: '◆', tag: '◇', doctor: '✚', tie: '♜', fingerprint: '◉', umbrella: '☂', money: '$',
  wallet: '▰', report: '▥', chart: '▧', clock: '◷', box: '□', shield: '◈', building: '▥', lock: '▣', search: '⌕', bell: '◔', menu: '☰',
};

function Icon({ name }: { name: string }) {
  return <span className="menu-icon" aria-hidden="true">{iconGlyphs[name] ?? '•'}</span>;
}

function Workspace() {
  const { user, logout, can } = useAuth();
  const scope = useScope();
  const i18n = useDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const allowed = (permission?: string) => !permission || can(permission);
  const activePath = location.pathname;
  const currentTitle = useMemo(() => {
    const all = [...directItems, ...groups.flatMap(group => group.items)];
    const hit = all.find(item => item.to.split(/[?#]/)[0] === activePath);
    return hit ? i18n.t(hit.label) : i18n.t('dashboard');
  }, [activePath, i18n]);

  const submitCustomerSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = customerSearch.trim();
    navigate(value ? `/crm/customers?q=${encodeURIComponent(value)}` : '/crm/customers');
    setMobileOpen(false);
  };

  const isItemActive = (item: MenuItem) => {
    const target = new URL(item.to, window.location.origin);
    if (target.pathname !== location.pathname) return false;
    if (target.search) return target.search === location.search;
    if (target.hash) return target.hash === location.hash || (item.to === '/operations#booking' && !location.hash);
    return true;
  };
  const renderDirect = (item: MenuItem) => allowed(item.permission) ? (
    <Link key={item.to} to={item.to} className={`sidebar-link ${isItemActive(item) ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>
      <Icon name={item.icon} /><span>{i18n.t(item.label)}</span>
    </Link>
  ) : null;

  return <div className={`legacy-shell ${mobileOpen ? 'sidebar-open' : ''}`}>
    <aside className="legacy-sidebar" aria-label="Main navigation">
      <div className="sidebar-brand">
        <Link to="/" onClick={() => setMobileOpen(false)}>
          <span className="brand-mark">W</span>
          <span><strong>WOW ME</strong><small>Operational System V5</small></span>
        </Link>
      </div>
      <nav className="sidebar-nav">
        {renderDirect(directItems[0])}
        {groups.slice(0, 2).map(group => {
          const visible = group.items.filter(item => allowed(item.permission));
          if (!visible.length || !allowed(group.permission)) return null;
          const open = visible.some(item => item.to.split(/[?#]/)[0] === activePath);
          return <details key={group.label} className="sidebar-group" open={open}>
            <summary><Icon name={group.icon} /><span>{i18n.t(group.label)}</span><span className="chevron">›</span></summary>
            <div className="sidebar-submenu">{visible.map(renderDirect)}</div>
          </details>;
        })}
        {renderDirect(directItems[1])}
        {groups.slice(2, 4).map(group => {
          const visible = group.items.filter(item => allowed(item.permission));
          if (!visible.length || !allowed(group.permission)) return null;
          const open = visible.some(item => item.to.split(/[?#]/)[0] === activePath);
          return <details key={group.label} className="sidebar-group" open={open}>
            <summary><Icon name={group.icon} /><span>{i18n.t(group.label)}</span><span className="chevron">›</span></summary>
            <div className="sidebar-submenu">{visible.map(renderDirect)}</div>
          </details>;
        })}
        {renderDirect(directItems[2])}
        {renderDirect(directItems[3])}
        {groups.slice(4).map(group => {
          const visible = group.items.filter(item => allowed(item.permission));
          if (!visible.length || !allowed(group.permission)) return null;
          const open = visible.some(item => item.to.split(/[?#]/)[0] === activePath);
          return <details key={group.label} className="sidebar-group" open={open}>
            <summary><Icon name={group.icon} /><span>{i18n.t(group.label)}</span><span className="chevron">›</span></summary>
            <div className="sidebar-submenu">{visible.map(renderDirect)}</div>
          </details>;
        })}
      </nav>
      <div className="sidebar-footer">
        <small>{i18n.t('signedInAs')}</small>
        <strong title={user?.email}>{user?.displayName}</strong>
      </div>
    </aside>

    <button className="sidebar-backdrop" aria-label="Close menu" onClick={() => setMobileOpen(false)} />

    <div className="legacy-wrapper">
      <header className="legacy-header">
        <div className="header-start">
          <button className="icon-button menu-toggle" onClick={() => setMobileOpen(value => !value)} aria-label="Toggle navigation"><Icon name="menu" /></button>
          <form className="customer-search" onSubmit={submitCustomerSearch}>
            <Icon name="search" />
            <input value={customerSearch} onChange={event => setCustomerSearch(event.target.value)} placeholder={i18n.t('customerSearch')} />
          </form>
        </div>
        <div className="header-actions">
          {can('organization.view') && <Link className="header-link" to="/admin/organization"><Icon name="cog" /><span>{i18n.t('settings')}</span></Link>}
          <button className="header-link" onClick={i18n.toggle}>{i18n.t('language')}</button>
          <button className="header-link notification-button" type="button" title={i18n.t('notifications')} aria-label={i18n.t('notifications')}><Icon name="bell" /></button>
          <div className="user-menu-wrap">
            <button className="header-link user-trigger" onClick={() => setUserMenuOpen(value => !value)}><span className="user-avatar">{user?.displayName?.slice(0, 1).toUpperCase() || 'U'}</span><span>{user?.displayName}</span><span className="caret">▾</span></button>
            {userMenuOpen && <div className="user-dropdown">
              <div className="user-dropdown-meta"><strong>{user?.displayName}</strong><small>{user?.email}</small></div>
              <button onClick={() => logout()}>{i18n.t('logout')}</button>
            </div>}
          </div>
        </div>
      </header>

      <div className="legacy-contextbar">
        <div className="breadcrumb"><span>{i18n.t('dashboard')}</span><b>/</b><strong>{currentTitle}</strong></div>
        <div className="scope">
          <label><span>{i18n.t('company')}</span><select value={scope.companyId} onChange={event => scope.setCompanyId(event.target.value)}><option value="">{i18n.t('select')}</option>{scope.companies.map(item => <option key={item.id} value={item.id}>{item.code} — {item.name}</option>)}</select></label>
          <label><span>{i18n.t('branch')}</span><select value={scope.branchId} onChange={event => scope.setBranchId(event.target.value)}><option value="">{i18n.t('allBranches')}</option>{scope.branches.map(item => <option key={item.id} value={item.id}>{item.code} — {item.name}</option>)}</select></label>
        </div>
      </div>

      <main className="legacy-main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/crm/leads" element={<LeadsPage />} />
          <Route path="/crm/leads/:id" element={<LeadDetailPage />} />
          <Route path="/crm/customers" element={<CustomersPage />} />
          <Route path="/crm/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/catalog/services" element={<ServicesPage />} />
          <Route path="/catalog/services/:id" element={<ServiceDetailPage />} />
          <Route path="/catalog/offers" element={<OffersPage />} />
          <Route path="/professionals" element={<ProfessionalsPage />} />
          <Route path="/operations" element={<OperationsPage />} />
          <Route path="/hr" element={<HrPage />} />
          <Route path="/marketing" element={<MarketingPage />} />
          <Route path="/reports" element={<ReportingPage />} />
          <Route path="/accounting" element={<AccountingPage />} />
          <Route path="/admin/organization" element={<OrganizationPage />} />
          <Route path="/admin/iam" element={<IamPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  </div>;
}

export function App() {
  const { user, ready } = useAuth();
  const { t } = useDirection();
  if (!ready) return <div className="center">{t('loading')}</div>;
  if (!user) return <LoginPage />;
  return <ScopeProvider><Workspace /></ScopeProvider>;
}
